import { z } from 'zod';

/**
 * The consent notice a publisher shows beside an interaction, and the one place it is made safe.
 *
 * A publisher needs a link in this text ("see our privacy notice"), so it cannot be plain text. It
 * is also the only field in the product where an editor's typing becomes markup, which makes it the
 * only place a cross site scripting bug could start. Two rules keep it closed:
 *
 * 1. It is sanitised on save, server side, and only the sanitised form is stored. Sanitising on the
 *    way out would mean the dangerous version is what is in the database, and one path that forgets
 *    to sanitise is then a live hole.
 * 2. The widget never receives markup. At publish time the sanitised text is converted to segments,
 *    which are plain data: a run of text, optionally with an href and optionally emphasised. The
 *    widget renders them as text nodes, so it needs no parser, no sanitiser and no `innerHTML`, none
 *    of which fits in a 30KB bundle rendering into a shadow root on somebody else's article. It is
 *    also what the rest of the published document already does: it carries data, not markup.
 *
 * The sanitiser is written as a parser rather than as a list of things to strip. Stripping is a
 * blocklist wearing a different hat, and every published bypass of a stripping sanitiser is an input
 * its author did not think of. Here the input is parsed into segments and the stored form is then
 * rendered back out of those segments, so anything the parser does not understand cannot survive:
 * it is either dropped or escaped into text. `sanitiseConsentText` is exactly
 * `renderConsentText(parseConsentText(raw))`, which is why the two cannot disagree.
 */

/** What an editor may type. Long enough for a sentence with a link, short enough to read. */
export const CONSENT_TEXT_MAX = 2000;

/**
 * What the sanitised form may occupy in the database.
 *
 * Larger than the input because escaping grows text: an editor typing 2000 ampersands stores 10,000
 * characters of `&amp;`. Nothing about it is a second limit on how much an editor can write.
 */
export const CONSENT_STORED_MAX = 12_000;

/** Bounded so a pathological input cannot make a published document large. */
const MAX_SEGMENTS = 50;

/**
 * The consent notice a reader sees when nobody has written one.
 *
 * Deliberately plain text with no link in it: we do not know a publisher's privacy notice url, and
 * a default that pointed at one would be inventing a legal statement on their behalf.
 */
export const DEFAULT_CONSENT_TEXT =
  'Your answers are recorded anonymously and counted towards the results shown here.';

/**
 * The newsletter wording a reader sees beside the tick when nobody has written one.
 *
 * A separate default from the one above because it is a separate purpose. The notice above explains
 * what happens to an answer; this one is the marketing consent a reader gives by ticking a box, and
 * under PECR it has to say what they are agreeing to receive and that they can stop. Plain text with
 * no link, for the same reason: we do not know a publisher's privacy notice url and will not invent
 * a legal statement on their behalf.
 */
export const DEFAULT_NEWSLETTER_CONSENT_TEXT =
  'Yes, email me the newsletter. I can unsubscribe at any time.';

/**
 * One run of consent text, as the widget receives it.
 *
 * `href` is always http or https, guaranteed by the parser rather than by the schema, and
 * `emphasis` is the only styling that survives. A segment is rendered as a text node, inside an
 * anchor and an emphasis element when it has them.
 */
export const consentSegmentSchema = z
  .object({
    text: z.string().min(1).max(CONSENT_STORED_MAX),
    href: z
      .string()
      .max(2048)
      .regex(/^https?:\/\//i, 'a consent link must be http or https'),
    emphasis: z.enum(['strong', 'em']),
  })
  .partial({ href: true, emphasis: true })
  .strict();

export type ConsentSegment = z.infer<typeof consentSegmentSchema>;

export const consentSegmentsSchema = z.array(consentSegmentSchema).max(MAX_SEGMENTS);

/** What an editor is allowed to send. The stored form is whatever sanitising this produces. */
export const consentTextInputSchema = z.string().max(CONSENT_TEXT_MAX);

/** What may appear in the stored form, and nothing else may. */
const ALLOWED_TAGS = new Set(['a', 'strong', 'em']);

/**
 * Tags whose contents are dropped along with the tag.
 *
 * Everything else unknown keeps its text and loses its markup, which is right for a `<p>` or a
 * `<span>` an editor pasted from a word processor. It is wrong for these: the body of a `<script>`
 * is code, not prose, and showing it to a reader as text would be nonsense rather than safety.
 */
const DROPPED_WITH_CONTENT = new Set([
  'script',
  'style',
  'noscript',
  'template',
  'title',
  'textarea',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
]);

interface OpenTag {
  readonly tag: string;
  readonly href?: string | undefined;
}

interface ParsedTag {
  readonly end: number;
  readonly closing: boolean;
  readonly name: string;
  readonly attributes: string;
}

/**
 * Parse consent text into segments.
 *
 * Everything the parser does not recognise loses its markup. A tag that is not in the allowlist is
 * dropped and its text kept; an attribute that is not `href` on an `<a>` is dropped; an `href` that
 * is not http or https is dropped, which leaves the anchor's text as plain text rather than
 * throwing it away. A `<` that does not begin a tag is text, exactly as a browser treats it, so
 * "under 5 < 6" survives being typed.
 */
export function parseConsentText(raw: string): ConsentSegment[] {
  const input = typeof raw === 'string' ? raw : '';
  const segments: ConsentSegment[] = [];
  const open: OpenTag[] = [];

  let buffer = '';
  let index = 0;

  const currentHref = (): string | undefined => {
    for (let i = open.length - 1; i >= 0; i -= 1) {
      const entry = open[i];
      if (entry?.tag === 'a' && entry.href !== undefined) return entry.href;
    }
    return undefined;
  };

  const currentEmphasis = (): 'strong' | 'em' | undefined => {
    for (let i = open.length - 1; i >= 0; i -= 1) {
      const tag = open[i]?.tag;
      if (tag === 'strong' || tag === 'em') return tag;
    }
    return undefined;
  };

  const flush = (): void => {
    if (buffer === '') return;
    const text = collapseWhitespace(decodeEntities(buffer));
    buffer = '';
    if (text === '') return;

    const href = currentHref();
    const emphasis = currentEmphasis();
    const previous = segments[segments.length - 1];

    // Adjacent runs with the same attributes are one run. Without this a dropped `<span>` in the
    // middle of a sentence would split it in two, and the stored form would not be canonical.
    if (previous && previous.href === href && previous.emphasis === emphasis) {
      segments[segments.length - 1] = { ...previous, text: previous.text + text };
      return;
    }
    if (segments.length >= MAX_SEGMENTS) return;

    segments.push({
      text,
      ...(href === undefined ? {} : { href }),
      ...(emphasis === undefined ? {} : { emphasis }),
    });
  };

  while (index < input.length) {
    const character = input.charAt(index);
    if (character !== '<') {
      buffer += character;
      index += 1;
      continue;
    }

    const tag = readTag(input, index);
    if (!tag) {
      // Not a tag at all, so it is what the editor typed.
      buffer += '<';
      index += 1;
      continue;
    }

    index = tag.end;
    flush();

    if (DROPPED_WITH_CONTENT.has(tag.name)) {
      if (!tag.closing) index = skipToClosingTag(input, tag.name, index);
      continue;
    }
    if (!ALLOWED_TAGS.has(tag.name)) continue;

    if (tag.closing) {
      closeTag(open, tag.name);
      continue;
    }

    open.push({
      tag: tag.name,
      href: tag.name === 'a' ? safeHref(readAttribute(tag.attributes, 'href')) : undefined,
    });
  }

  flush();

  return trimEnds(segments);
}

/** Segments back to the one markup shape this module ever produces. */
export function renderConsentText(segments: readonly ConsentSegment[]): string {
  return segments
    .map((segment) => {
      let html = escapeHtml(segment.text);
      if (segment.emphasis !== undefined) {
        html = `<${segment.emphasis}>${html}</${segment.emphasis}>`;
      }
      // The anchor is always outermost, so that `<strong><a>` and `<a><strong>` store identically
      // and sanitising an already sanitised string is a no-op.
      if (segment.href !== undefined) html = `<a href="${escapeHtml(segment.href)}">${html}</a>`;
      return html;
    })
    .join('');
}

/**
 * What is stored. Parse, then render: nothing can survive that the parser did not understand.
 *
 * Idempotent, which matters because the stored form is parsed again at publish time and re-saving
 * an unchanged brand must not slowly rewrite the text.
 */
export function sanitiseConsentText(raw: string): string {
  return renderConsentText(parseConsentText(raw));
}

/**
 * The wording as one string, which is exactly what a reader is shown and what the widget posts.
 *
 * The widget renders the segments in order as text nodes and, when it sends the consent evidence
 * with a signup, joins that same list with no separator between the runs. Joining here rather than
 * reaching for the stored markup is what makes the two comparable at all: the stored form carries
 * `<a href="...">` and a reader was never shown a tag.
 */
export function consentSegmentsText(segments: readonly ConsentSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

/**
 * Whether the wording posted with a signup is the wording that was published.
 *
 * A reader's browser says what it showed, and without this it would be believed: anyone could post
 * a sentence of their own choosing and have it filed as the evidence of what they agreed to, which
 * is the one field in a subscription whose whole value is that it is not the caller's claim.
 *
 * Whitespace is collapsed on both sides and nothing else is. The parser already collapses runs of
 * whitespace when it makes segments, so in the ordinary path the two strings are identical and this
 * changes nothing; it is here so that a widget that joined the runs with a space, or a reader's
 * browser that normalised the text on the way out of the DOM, is not refused over a space. Every
 * other difference is refused, because a different word is a different statement.
 *
 * Empty published wording never matches. With nothing to show there is nothing that could honestly
 * be recorded, which is the same rule the widget applies when it decides not to draw the step.
 */
export function consentTextMatches(posted: string, segments: readonly ConsentSegment[]): boolean {
  const published = forComparison(consentSegmentsText(segments));
  return published !== '' && forComparison(posted) === published;
}

function forComparison(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Whether a live interaction's frozen consent text has fallen behind its brand's current text.
 *
 * Absent means it was published before consent text existed, which is stale rather than fine: what
 * that version's readers were shown is not what the brand says now.
 */
export function consentIsStale(published: string | undefined, current: string): boolean {
  return (published ?? '') !== current;
}

/**
 * An href a browser would treat as http or https, and nothing else.
 *
 * Two decodings happen before the scheme is looked at, because a browser does both and a check that
 * skipped either would be reading a different url from the one that would be followed:
 *
 * - HTML entities, so `java&#115;cript:` is `javascript:` here as well. Decoded once and never in a
 *   loop: a browser decodes once too, so `&amp;#115;` really is the literal text `&#115;`.
 * - Whitespace and control characters, which a browser strips out of a url before deciding what the
 *   scheme is, so a newline or a space inside `java script:` does not hide it.
 *
 * Then it is an allowlist of two schemes rather than a blocklist of the dangerous ones. `data:` and
 * `javascript:` are the two everybody remembers; `vbscript:`, `blob:` and `filesystem:` are the ones
 * a blocklist forgets.
 */
export function safeHref(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;

  const stripped = stripUrlNoise(decodeEntities(value));
  if (!/^https?:\/\//i.test(stripped)) return undefined;

  try {
    const url = new URL(stripped);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    if (url.hostname === '') return undefined;
    if (url.href.length > 2048) return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

/**
 * Read one tag starting at `<`.
 *
 * Quoted attribute values are tracked, because `<a href="a>b">` does not end at the first `>` and a
 * regex that assumed it did would leave the rest of the attribute as text a browser would not show.
 * Returns null when what follows `<` is not a tag name, which is the "5 < 6" case.
 */
function readTag(input: string, start: number): ParsedTag | null {
  let index = start + 1;
  let closing = false;

  if (input[index] === '/') {
    closing = true;
    index += 1;
  }

  const nameStart = index;
  while (index < input.length && /[a-zA-Z0-9]/.test(input.charAt(index))) index += 1;
  if (index === nameStart) return null;

  const name = input.slice(nameStart, index).toLowerCase();
  const attributeStart = index;
  let quote: string | undefined;

  while (index < input.length) {
    const character = input.charAt(index);
    if (quote !== undefined) {
      if (character === quote) quote = undefined;
    } else if (character === '"' || character === "'") {
      quote = character;
    } else if (character === '>') {
      return { end: index + 1, closing, name, attributes: input.slice(attributeStart, index) };
    }
    index += 1;
  }

  // An unclosed tag swallows the rest of the input rather than becoming visible text. A browser
  // does the same, and text that only appears because the editor forgot a `>` is not consent text.
  return { end: input.length, closing, name, attributes: input.slice(attributeStart) };
}

function skipToClosingTag(input: string, name: string, from: number): number {
  const at = input.toLowerCase().indexOf(`</${name}`, from);
  if (at === -1) return input.length;
  const tag = readTag(input, at);
  return tag ? tag.end : input.length;
}

/**
 * Pop back to the matching open tag.
 *
 * Everything opened inside it closes with it, which is how `<strong><a></strong>` ends up with the
 * anchor closed rather than leaking its href over the rest of the sentence. A close tag matching
 * nothing is ignored.
 */
function closeTag(open: OpenTag[], name: string): void {
  for (let i = open.length - 1; i >= 0; i -= 1) {
    if (open[i]?.tag === name) {
      open.length = i;
      return;
    }
  }
}

/** One attribute by name, case insensitively, quoted or not. */
function readAttribute(source: string, wanted: string): string | undefined {
  const pattern = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*("[^"]*"|'[^']*'|[^\s"'`=<>]+))?/g;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source)) !== null) {
    if (match[1]?.toLowerCase() !== wanted) continue;
    const value = match[2];
    if (value === undefined) return undefined;
    const quoted = value.startsWith('"') || value.startsWith("'");
    return quoted ? value.slice(1, -1) : value;
  }
  return undefined;
}

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  // Decoded to an ordinary space: the collapse below treats the two alike anyway.
  nbsp: ' ',
};

/**
 * Decode HTML entities once.
 *
 * The semicolon is optional because browsers accept `&#115` without one in an attribute value, and
 * a decoder that required it would read a different string from the one a browser would follow.
 */
function decodeEntities(value: string): string {
  return value.replace(
    /&(#[xX][0-9a-fA-F]{1,6}|#\d{1,7}|[a-zA-Z][a-zA-Z0-9]{1,31});?/g,
    (match, body: string) => {
      if (body.startsWith('#')) {
        const hex = body[1] === 'x' || body[1] === 'X';
        const code = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
        if (!Number.isInteger(code) || code <= 0 || code > 0x10ffff) return match;
        // Lone surrogates are not characters and String.fromCodePoint would produce a broken one.
        if (code >= 0xd800 && code <= 0xdfff) return match;
        return String.fromCodePoint(code);
      }
      return NAMED_ENTITIES[body.toLowerCase()] ?? match;
    },
  );
}

/**
 * Control characters out, runs of whitespace to one space.
 *
 * A consent notice is one line of prose. Collapsing is what a browser does when it renders the
 * markup anyway, and doing it here means the stored form is canonical and a newline cannot be used
 * to hide part of the text from whoever reviews it.
 */
function collapseWhitespace(text: string): string {
  let kept = '';
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    // Tab, newline and carriage return stay: they are whitespace, and the collapse below folds
    // them into a single space along with everything else.
    const whitespace = code === 0x09 || code === 0x0a || code === 0x0d;
    if (!whitespace && isInvisible(code)) continue;
    kept += character;
  }
  return kept.replace(/\s+/g, ' ');
}

/** Trim the outside of the whole notice, without touching the spaces that sit between segments. */
function trimEnds(segments: ConsentSegment[]): ConsentSegment[] {
  const trimmed = [...segments];

  const first = trimmed[0];
  if (first) trimmed[0] = { ...first, text: first.text.replace(/^\s+/, '') };

  const lastIndex = trimmed.length - 1;
  const last = trimmed[lastIndex];
  if (last) trimmed[lastIndex] = { ...last, text: last.text.replace(/\s+$/, '') };

  return trimmed.filter((segment) => segment.text !== '');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Characters removed from a url before its scheme is read.
 *
 * A browser ignores these when it works out what scheme a url has, so a tab or a newline inside
 * "javascript:" does not stop it being javascript, and a check that read the raw string would be
 * reading a different url from the one that would be followed.
 */
function stripUrlNoise(value: string): string {
  let kept = '';
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    // Space and the C0 controls, which is what "whitespace inside the scheme" means in practice.
    if (code <= 0x20) continue;
    if (isInvisible(code)) continue;
    kept += character;
  }
  return kept;
}

/**
 * Characters with no visible width, which is what makes them useful for hiding things.
 *
 * DEL and the C1 controls, the zero width and bidirectional marks, the line and paragraph
 * separators, the bidirectional overrides, the word joiner and the byte order mark.
 */
function isInvisible(code: number): boolean {
  if (code >= 0x7f && code <= 0x9f) return true;
  if (code >= 0x200b && code <= 0x200f) return true;
  if (code >= 0x2028 && code <= 0x202e) return true;
  return code === 0x2060 || code === 0xfeff;
}
